import { GeckoBinnedDataBuffer } from 'features/dataLayers'
import {
  FigureSelectionArea,
  measureToXBinIndex,
  measureToYBinIndex,
  xBinIndexToMeasure,
  yBinIndexToMeasure,
} from 'features/fde'
import { CriticalProfilePoint } from 'features/indicationTriage/cards/criticalProfile/criticalProfileApi'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { computeMtCylindrical } from 'features/indicationTriage/cards/ffsCalc/foliasProfileLookup'
import { computeLambda } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsDataValuesSection'
import first from 'lodash/first'
import isNaN from 'lodash/isNaN'
import isNil from 'lodash/isNil'
import last from 'lodash/last'
import range from 'lodash/range'
import slice from 'lodash/slice'
import sum from 'lodash/sum'
import uniqBy from 'lodash/uniqBy'
import {
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

export function getMinPointIndex(
  points: (number | undefined)[]
): number | undefined {
  if (!points?.length) {
    return undefined
  }
  let minIdx = -1
  points.forEach(
    (point, idx) =>
      (minIdx =
        isNil(points[minIdx]) || (point && point < points[minIdx]!)
          ? idx
          : minIdx)
  )
  return minIdx < 0 ? undefined : minIdx
}

export function computeCriticalPath(
  area: FigureSelectionArea,
  cdf: GeckoComponentDataFrame,
  cdfData: GeckoBinnedDataBuffer['values'],
  axis: 'x' | 'y'
): ReadonlyArray<CriticalProfilePoint> {
  const xStart = area.x.start
  const yStart = area.y.start
  const xEnd = area.x.end
  const yEnd = area.y.end

  const xBinStart = Math.round(measureToXBinIndex(xStart, cdf))
  const xBinEnd = Math.round(measureToXBinIndex(xEnd, cdf))
  const yBinStart = Math.round(measureToYBinIndex(yStart, cdf))
  const yBinEnd = Math.round(measureToYBinIndex(yEnd, cdf))

  const getPoint: (x: number, y: number) => number | undefined = (x, y) => {
    return cdfData.at(y * cdf.data.binning.x_bin_count + x)
  }

  const idxRange =
    axis === 'x' ? range(xBinStart, xBinEnd) : range(yBinStart, yBinEnd)
  const getSegment: (idx: number) => (number | undefined)[] =
    axis === 'x'
      ? (x) => range(yBinStart, yBinEnd).map((y) => getPoint(x, y))
      : (y) => range(xBinStart, xBinEnd).map((x) => getPoint(x, y))

  const cdfValueUnit = cdf.data.params.measurement_unit
  return idxRange
    .map(getSegment)
    .map((segment, indexPos) => {
      const valuePos = getMinPointIndex(segment)
      const value = !isNil(valuePos) ? segment[valuePos] : undefined
      if (isNil(valuePos) || isNil(value) || isNaN(value)) {
        return undefined
      }
      const meas = isNil(value)
        ? undefined
        : GeckoMeasurement.CreateFromValue(value, cdfValueUnit)

      const xMeas =
        axis === 'x'
          ? xBinIndexToMeasure(idxRange[indexPos], cdf)
          : xBinIndexToMeasure(xBinStart + valuePos, cdf)
      const yMeas =
        axis === 'x'
          ? yBinIndexToMeasure(yBinStart + valuePos, cdf)
          : yBinIndexToMeasure(idxRange[indexPos], cdf)
      return {
        coords: GeckoComponentCoords.CreateFromMeasurements(
          cdf.tags.component,
          xMeas,
          yMeas
        ),
        value: meas,
      }
    })
    .filter((item): item is NonNullable<typeof item> => !isNil(item))
}

export interface ComputeSlicesArgs {
  criticalPath: ReadonlyArray<CriticalProfilePoint>
  startIdx: number
  tc: GeckoMeasurement
  axis: 'x' | 'y'
  fca: GeckoMeasurement
  innerDiameter: GeckoMeasurement
  /**
   * If true, includes single-point slice (just the minimum point itself).
   * Defaults to false for backwards compatibility.
   */
  includeSinglePointSlice?: boolean
}

export interface CtpSlice {
  si_s: GeckoMeasurement
  si_e: GeckoMeasurement
  si: GeckoMeasurement
  lambda: GeckoMeasurement
  mt: GeckoMeasurement
  ai: number
  ai0: number
  rsf: GeckoMeasurement
  idx_s: number
  idx_e: number
}

export function computeSlices({
  criticalPath,
  startIdx,
  tc,
  axis,
  fca,
  innerDiameter,
  includeSinglePointSlice = false,
}: ComputeSlicesArgs): ReadonlyArray<CtpSlice> {
  const startRadius = includeSinglePointSlice ? 0 : 1

  return uniqBy(
    range(startRadius, criticalPath.length).map((i) => [
      Math.max(startIdx - i, 0),
      Math.min(startIdx + i, criticalPath.length - 1),
    ]),
    ([minIdx, maxIdx]) => `${minIdx}:${maxIdx}`
  ).map(([sliceMinIdx, sliceMaxIdx]) => {
    // Note: _.slice is exclusive, so we need to add 1 to the maxIdx
    const slicePoints = slice(criticalPath, sliceMinIdx, sliceMaxIdx + 1)

    const pathMin = first(criticalPath)!.coords[axis]
    const [si_s, si_e] = [first(slicePoints)!, last(slicePoints)!]
      .map((point) => point.coords[axis])
      .map((s) => s.minus(pathMin))
    const si = si_e.minus(si_s)

    const lambda = computeLambda(innerDiameter, si, tc)
    const mt = GeckoMeasurement.CreateFromValue(
      computeMtCylindrical(lambda.toNumber(GeckoMeasurementUnit.Ratio)),
      GeckoMeasurementUnit.Ratio
    )

    const segmentIdxs =
      slicePoints.length > 1 ? range(1, slicePoints.length) : []
    const metalArea = sum(
      segmentIdxs.map((segI) => {
        const [prev, curr] = [slicePoints[segI - 1], slicePoints[segI]]
        const [prevValue, currValue] = [prev, curr].map((crit) => crit.value)
        if (isNil(prevValue) || isNil(currValue)) {
          console.warn()
          return 0
        }
        return computeSegmentArea(
          prev.coords[axis].toNumber(WORKING_UNIT_THICKNESS),
          prevValue.minus(fca).toNumber(WORKING_UNIT_THICKNESS),
          curr.coords[axis].toNumber(WORKING_UNIT_THICKNESS),
          currValue.minus(fca).toNumber(WORKING_UNIT_THICKNESS)
        )
      })
    )

    // Source: API 579 Part 5, Section 5.4.3.2 Step 8, Equation 5.19
    const ai0 =
      si.toNumber(WORKING_UNIT_THICKNESS) * tc.toNumber(WORKING_UNIT_THICKNESS)
    // Cross hatched area
    const ai = ai0 - metalArea
    const rsfNumerator = 1 - ai / ai0
    const rsfDenominator =
      1 - (1 / mt.toNumber(GeckoMeasurementUnit.Ratio)) * (ai / ai0)
    const rsf = GeckoMeasurement.CreateFromValue(
      rsfNumerator / rsfDenominator,
      GeckoMeasurementUnit.Ratio
    )
    return {
      si_s,
      si_e,
      lambda,
      mt,
      ai,
      ai0,
      rsf,
      si,
      idx_s: sliceMinIdx,
      idx_e: sliceMaxIdx,
    }
  })
}

// - - - - - - - - - - - - - - - - Average Thickness Calculation Helper Functions - - - - - - - - - - - - - - - -
export function computeLinearInterpolation(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x: number
): number {
  return y1 + ((y2 - y1) * (x - x1)) / (x2 - x1)
}

// Uses the trapezoid area formula
export function computeSegmentArea(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const triangleHeight = Math.abs(y2 - y1)
  const triangleWidth = Math.abs(x2 - x1)
  const rectHeight = Math.min(y1, y2)
  const triangleArea = (triangleHeight * triangleWidth) / 2
  const rectArea = rectHeight * triangleWidth
  return triangleArea + rectArea
}
